import { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import BlobStorageCalc from '../components/calculators/BlobStorageCalc';
import { Link } from 'react-router-dom';
import { CalculatorContentWrapper } from '../components/shared/CalculatorContentWrapper';
import { useCalculatorStore } from '../store/calculatorStore';
import { parseURLToBlob } from '../utils/urlState';
import { generateBreadcrumbList } from '../utils/schemaHelpers';
import { Breadcrumbs } from '../components/shared/Breadcrumbs';

export default function BlobStoragePage() {
  const setBlobInputs = useCalculatorStore(s => s.setBlobInputs);

  useEffect(() => {
    const urlInputs = parseURLToBlob();
    if (urlInputs) {
      setBlobInputs(urlInputs as any);
    }
  }, [setBlobInputs]);
  return (
    <div className="px-6 py-6 w-full">
      <Helmet>
        <title>Azure Blob Storage Calculator UK | AzureCalc.uk</title>
        <meta name="description" content="Calculate Azure Blob Storage costs for UK South. Compare Hot, Cool, Cold and Archive tier pricing in GBP. Updated nightly from the Azure Retail Prices API." />
        <link rel="canonical" href="https://www.azure-calc.co.uk/blob-storage" />
        <meta property="og:title" content="Azure Blob Storage Calculator UK" />
        <meta property="og:description" content="Free independent Azure Blob Storage pricing for UK South. Compare all tiers in GBP." />
        <meta property="og:url" content="https://www.azure-calc.co.uk/blob-storage" />
        <meta property="og:image" content="https://www.azure-calc.co.uk/og/azure-calc-og.png" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'HowTo',
          name: 'How to estimate Azure Blob Storage costs for UK South',
          description: 'Use the AzureCalc.uk calculator to estimate your Azure Blob Storage monthly costs in GBP.',
          step: [
            { '@type': 'HowToStep', position: 1, name: 'Select a storage tier', text: 'Choose Hot, Cool, Cold, or Archive depending on how frequently you access your data.' },
            { '@type': 'HowToStep', position: 2, name: 'Enter your storage volume', text: 'Input the total GB you plan to store per month.' },
            { '@type': 'HowToStep', position: 3, name: 'Set your operation counts', text: 'Enter your expected read, write, and list operation counts per month.' },
            { '@type': 'HowToStep', position: 4, name: 'Review the cost breakdown', text: 'The calculator shows a line-by-line GBP breakdown and a comparison across all tiers.' },
          ],
        })}</script>
        <script type="application/ld+json">{JSON.stringify(generateBreadcrumbList([{ name: 'Home', url: '/', position: 1 }, { name: 'Blob Storage Calculator', url: '/blob-storage', position: 2 }]))}</script>
      </Helmet>

      <div className="pb-6 mb-8" style={{ borderBottom: '1px solid var(--outline-variant)' }}>
        <Breadcrumbs />
        <h1 className="text-2xl font-bold mb-2" style={{ color: 'var(--text-primary)' }}>Azure Blob Storage Cost Calculator</h1>
        <p className="text-sm" style={{ color: 'var(--text-tertiary)' }}>UK South pricing · Hot, Cool, Cold &amp; Archive tiers · Operations · Redundancy</p>
      </div>

      <BlobStorageCalc />

      <CalculatorContentWrapper
        eyebrow="Storage Pricing"
        title="Blob Storage costs are usually decided by access pattern, not just capacity"
        intro={
          <>
            <p className="mb-3">
              Blob Storage often looks cheap when a team only multiplies stored gigabytes by a tier
              price. In reality, Azure storage spend is shaped by access tier, redundancy, transaction
              volume, retrieval behavior, and lifecycle design. A storage account that looks efficient
              on paper can become expensive when data is written, rehydrated, or replicated more often
              than the architecture expected.
            </p>
            <p>
              This calculator remains the primary action on the page, but the sections below explain
              how to read the output and where hidden storage costs usually appear.
            </p>
          </>
        }
        sections={[
          {
            id: 'tiering',
            title: 'Tier choice is a policy decision, not a cosmetic setting',
            body: (
              <>
                <p className="mb-3">
                  Hot, Cool, Cold, and Archive exist because Azure charges differently depending on how
                  quickly data must be available and how often it will be accessed. Hot is designed for
                  frequently used content, while colder tiers trade lower stored-GB pricing for more
                  expensive access, longer retrieval constraints, or both.
                </p>
                <p>
                  The right tier is therefore about workload behavior, not optimism. If data is called
                  archive but is regularly retrieved for analytics, the lower capacity rate can be
                  wiped out by access and rehydration charges.
                </p>
              </>
            ),
          },
          {
            id: 'redundancy',
            title: 'Redundancy choice changes storage economics as well as resilience',
            body: (
              <>
                <p className="mb-3">
                  LRS, ZRS, GRS, and RA-GRS are resilience decisions, but they are also budget
                  decisions. More replication usually means more spend because you are paying for how
                  Azure protects and stores the data underneath the service. That can be the correct
                  architecture, but it should be deliberate.
                </p>
                <p>
                  If your estimate is built on local redundancy and your platform standard later
                  requires geo-redundancy, the final monthly cost can move materially even when data
                  volume stays unchanged.
                </p>
              </>
            ),
          },
          {
            id: 'retrieval',
            title: 'Retrieval and transaction costs are where teams get surprised',
            body: (
              <>
                <p className="mb-3">
                  Storage invoices often rise because the workload performs more reads, writes, or
                  list operations than expected. Colder tiers reduce the cost of bytes at rest but
                  often increase the cost of getting data back out. A backup, reporting, or ETL job
                  that scans large collections can turn a cheap archive strategy into an expensive
                  operating pattern.
                </p>
                <p>
                  The useful budgeting question is not only how much data you store, but also how often
                  you touch it and what tier those touches happen against.
                </p>
              </>
            ),
          },
          {
            id: 'worked-example',
            title: 'Worked example: why a cheaper tier can still raise the bill',
            body: (
              <>
                <p className="mb-3">
                  Imagine a team moves several terabytes from Hot to Cool because the stored-GB rate
                  looks lower. If monthly reporting still reads a large portion of the dataset and
                  ingestion continues with frequent writes, the savings from capacity can be eroded by
                  transaction and retrieval charges. The nominally cheaper tier becomes a worse fit.
                </p>
                <p>
                  This is why the calculator includes operations as first-class inputs. It helps reveal
                  whether your cost driver is storage volume, access frequency, or a mismatch between
                  lifecycle assumptions and real usage.
                </p>
              </>
            ),
          },
          {
            id: 'hidden-costs',
            title: 'Hidden storage cost patterns to watch for',
            body: (
              <ul className="list-disc pl-5 space-y-2">
                <li>Lifecycle rules that move data to a colder tier and then pull it back too often.</li>
                <li>Replication choices that are made by policy but omitted from early calculator assumptions.</li>
                <li>Large-scale list and read activity from analytics jobs, indexing pipelines, or backup validation.</li>
                <li>Assuming archive-like access patterns for datasets that still support operational reporting.</li>
                <li>Budgeting storage only and forgetting egress, monitoring, and downstream processing costs.</li>
              </ul>
            ),
          },
          {
            id: 'faq',
            title: 'Quick FAQ',
            body: (
              <>
                <p className="mb-2"><strong>Is the cheapest tier always best for backups?</strong> No. It depends on retention, restore speed expectations, and how often recovery data is actually accessed.</p>
                <p className="mb-2"><strong>Why does operation count matter so much?</strong> Because Blob Storage is not billed only on capacity. Transactions and retrieval activity can become a major share of spend.</p>
                <p><strong>What should you validate after the estimate?</strong> Real read and write behavior, redundancy policy, lifecycle transitions, and any expected retrieval or rehydration patterns.</p>
              </>
            ),
          },
        ]}
        methodologyNote={
          <>
            <p className="mb-2">
              AzureCalc.uk uses UK South retail pricing in GBP and surfaces calculator inputs that map
              to the underlying Azure billing model. The methodology page explains data provenance,
              refresh cadence, and why agreement discounts or region changes can alter a real invoice.
            </p>
            <Link to="/methodology" className="text-[#0078d4] hover:underline">
              See the pricing methodology →
            </Link>
          </>
        }
        relatedLinks={[
          { label: 'Blob Storage Pricing Guide', to: '/guides/azure-blob-storage-pricing-uk' },
          { label: 'Bandwidth Calculator', to: '/bandwidth' },
          { label: 'FAQ', to: '/faq' },
          { label: 'Homepage', to: '/' },
        ]}
      />

      <div className="mt-12 pt-8" style={{ borderTop: '1px solid var(--outline-variant)' }}>
        <h2 className="text-[13px] font-bold uppercase tracking-widest mb-4" style={{ color: 'var(--text-tertiary)' }}>Related guides</h2>
        <Link
          to="/guides/azure-blob-storage-pricing-uk"
          className="flex items-center gap-3 p-4 rounded transition-colors group"
          style={{ backgroundColor: 'var(--surface-container)', border: '1px solid var(--outline-variant)' }}
        >
          <span className="material-symbols-outlined text-[#0078d4] text-[20px]" style={{ fontVariationSettings: "'FILL' 1" }}>menu_book</span>
          <div>
            <p className="text-[14px] font-semibold group-hover:text-white transition-colors" style={{ color: 'var(--text-secondary)' }}>Azure Blob Storage Pricing UK 2026</p>
            <p className="text-[12px]" style={{ color: 'var(--text-tertiary)' }}>Hot vs Cool vs Cold vs Archive — when to use each tier and how the costs stack up</p>
          </div>
          <span className="material-symbols-outlined text-[#0078d4] text-[16px] ml-auto">arrow_forward</span>
        </Link>
      </div>
    </div>
  );
}
